import { useMemo } from "react";

import { isSingleFieldOutcomes } from "utils";

import { KG_PER_TONNE } from "consts";

import { CarbonProgramOutcomesResponse, PRACTICE_SLUG, SingleFieldOutcomes, ValidAggregation } from "models";

export type FieldImpact = {
  payoutMin: number;
  payoutMax: number;
  payoutAvg: number;
  co2Min: number;
  co2Max: number;
  co2Avg: number;
};

export type CarbonProgramImpactResult = {
  byField: Record<string, FieldImpact>;
  baselineFootprints: Record<number, number>;
  /** Minimum total payout across all fields - used as payout in Soil+3.0. */
  totalPayoutMin: number;
  /** Maximum total payout across all fields - used as potentialPayout in Soil+3.0. */
  totalPayoutMax: number;
  /**
   * Average total payout across all fields.
   * @deprecated Up from Soil+3.0 to use totalPayoutMin and totalPayoutMax instead.
   */
  totalPayoutAvg: number;
  /** Minimum total CO2 impact (tonnes) across all fields - used as co2Impact in Soil+3.0. */
  totalCo2Min: number;
  /** Maximum total CO2 impact (tonnes) across all fields - used as potentialCo2Impact in Soil+3.0. */
  totalCo2Max: number;
  /**
   * Average total CO2 impact (tonnes) across all fields.
   * @deprecated Up from Soil+3.0 to use totalCo2Min and totalCo2Max instead.
   */
  totalCo2Avg: number;
  allotmentAmount: number;
  bonusAllotmentAmount: number;
  showPayouts: boolean;
};

const EMPTY_RESULT: CarbonProgramImpactResult = {
  byField: {},
  baselineFootprints: {},
  totalPayoutMin: 0,
  totalCo2Min: 0,
  totalPayoutMax: 0,
  totalCo2Max: 0,
  totalCo2Avg: 0,
  totalPayoutAvg: 0,
  allotmentAmount: 0,
  bonusAllotmentAmount: 0,
  showPayouts: false,
};

const extractBaselineFootprints = (allFieldOutcomes: SingleFieldOutcomes[]): Record<number, number> => {
  const yearsToValues: Record<number, number> = {};

  allFieldOutcomes.forEach((field) => {
    const cropsOutcome = field.fieldOutcomes[PRACTICE_SLUG.CROPS];
    if (!cropsOutcome?.isParticipating) return;

    Object.entries(cropsOutcome.years ?? {}).forEach(([yearKey, aggregationByYear]) => {
      const year = Number(yearKey);
      const yearData = aggregationByYear as ValidAggregation;
      if (!yearData?.validation?.valid || !yearData.isBaselineYear) return;

      const soc = yearData.footprints?.amount?.soc?.min;
      const emission = yearData.footprints?.amount?.emission?.min;
      if (soc === null || emission === null || soc === undefined || emission === undefined) return;

      yearsToValues[year] = (yearsToValues[year] ?? 0) + emission + soc;
    });
  });

  return Object.fromEntries(Object.entries(yearsToValues).map(([year, value]) => [Number(year), Math.round(value / KG_PER_TONNE)]));
};

/**
 * Aggregates payout and CO2 impact per field, then across all fields, for a given year.
 *
 * Payouts are returned in the same unit the API provides (the monetary amount for the field).
 * CO2 values are converted from kg (API) to tonnes.
 */
export const computeCarbonProgramImpact = (carbonProgramOutcomes: CarbonProgramOutcomesResponse, year: number): CarbonProgramImpactResult => {
  const allResults = Object.values(carbonProgramOutcomes);
  if (allResults.length === 0) return EMPTY_RESULT;

  const singleFieldOutcomes: SingleFieldOutcomes[] = allResults.filter(isSingleFieldOutcomes);
  if (singleFieldOutcomes.length === 0) return EMPTY_RESULT;

  const byField: Record<string, FieldImpact> = {};

  let totalPayoutMin = 0;
  let totalPayoutMax = 0;
  let totalPayoutAvg = 0;
  let totalCo2Min = 0;
  let totalCo2Max = 0;
  let totalCo2Avg = 0;

  singleFieldOutcomes.forEach((field) => {
    let fieldPayoutMin = 0;
    let fieldPayoutMax = 0;
    let fieldPayoutAvg = 0;
    let fieldCo2Min = 0;
    let fieldCo2Max = 0;
    let fieldCo2Avg = 0;

    Object.values(PRACTICE_SLUG).forEach((practiceSlug) => {
      const practiceOutcome = field.fieldOutcomes[practiceSlug];
      if (!practiceOutcome?.isParticipating) return;

      const yearData = practiceOutcome.years?.[year] as ValidAggregation | undefined;
      if (!yearData?.validation?.valid || yearData.isBaselineYear) return;

      if (yearData.payouts) {
        fieldPayoutMin += yearData.payouts.min;
        fieldPayoutMax += yearData.payouts.max;
        fieldPayoutAvg += yearData.payouts.avg;
      }

      if (yearData.points) {
        const { removals, avoidances, reductions } = yearData.points;
        fieldCo2Min += removals.min + avoidances.min + reductions.min;
        fieldCo2Max += removals.max + avoidances.max + reductions.max;
        fieldCo2Avg += removals.avg + avoidances.avg + reductions.avg;
      }
    });

    const fieldCo2MinTonnes = fieldCo2Min / KG_PER_TONNE;
    const fieldCo2MaxTonnes = fieldCo2Max / KG_PER_TONNE;
    const fieldCo2AvgTonnes = fieldCo2Avg / KG_PER_TONNE;

    byField[field.fieldId] = {
      payoutMin: fieldPayoutMin,
      payoutMax: fieldPayoutMax,
      payoutAvg: fieldPayoutAvg,
      co2Min: fieldCo2MinTonnes,
      co2Max: fieldCo2MaxTonnes,
      co2Avg: fieldCo2AvgTonnes,
    };

    totalPayoutMin += fieldPayoutMin;
    totalPayoutMax += fieldPayoutMax;
    totalPayoutAvg += fieldPayoutAvg;
    totalCo2Min += fieldCo2MinTonnes;
    totalCo2Max += fieldCo2MaxTonnes;
    totalCo2Avg += fieldCo2AvgTonnes;
  });

  const baselineFootprints = extractBaselineFootprints(singleFieldOutcomes);
  const allotmentAmount = singleFieldOutcomes.find((f) => f.soilPlus3Data)?.soilPlus3Data?.errAllotment ?? 0;
  const bonusAllotmentAmount = singleFieldOutcomes.find((f) => f.soilPlus3Data)?.soilPlus3Data?.bonusAllotmentAmount ?? 0;

  return {
    byField,
    baselineFootprints,
    totalPayoutMin,
    totalPayoutMax,
    totalPayoutAvg,
    totalCo2Min,
    totalCo2Max,
    totalCo2Avg,
    allotmentAmount,
    bonusAllotmentAmount,
    showPayouts: allotmentAmount > 0,
  };
};

export const useCarbonProgramImpact = (carbonProgramOutcomes: CarbonProgramOutcomesResponse, year: number): CarbonProgramImpactResult =>
  useMemo(() => computeCarbonProgramImpact(carbonProgramOutcomes, year), [carbonProgramOutcomes, year]);
